// Offscreen sandbox for scanning raw HTML fragments authored in a code editor
// (hub custom header/footer, account-level project header/footer).
//
// The fragment is rendered into a hidden same-origin iframe rather than the
// admin document so the customer's unscoped HTML/CSS cannot restyle the admin
// UI. Portal stylesheets and custom CSS are mirrored in so axe resolves the
// same colours the live page would produce.

import { sanitizeScanHtml } from "./sanitizeScanHtml";

export interface HtmlFragmentHostOptions {
  /** Markup to scan, already wrapped in whatever the live page wraps it in. */
  html: string;
  /** Stable element id so repeat scans of the same field reuse one iframe. */
  hostId: string;
  /** Root element id to mirror (e.g. "hub-index") so id-scoped CSS resolves. */
  rootId?: string;
  /** Document title - never user-visible, but keeps the sandbox valid HTML. */
  title?: string;
  /** Serialized <link rel="stylesheet"> markup for the target public layout. */
  styleLinks?: string;
  /** Customer custom CSS, injected via textContent so a stray </style> is inert. */
  customCss?: string;
}

const STYLESHEET_POLL_MS = 50;
const STYLESHEET_POLL_ATTEMPTS = 50; // ~2.5s cap

function ensureHost(hostId: string): HTMLIFrameElement {
  const existing = document.getElementById(hostId);
  if (existing) return existing as HTMLIFrameElement;

  const iframe = document.createElement("iframe");
  iframe.id = hostId;
  iframe.title = "Accessibility scan sandbox";
  iframe.setAttribute("inert", "");
  iframe.setAttribute("sandbox", "allow-scripts allow-same-origin");
  // Offscreen but laid out (not display:none) so axe can resolve contrast.
  iframe.style.cssText =
    "position:fixed;left:-10000px;top:0;width:1200px;height:800px;border:0;";
  document.body.appendChild(iframe);
  return iframe;
}

// Resolves once every <link rel="stylesheet"> has parsed, or the cap elapses.
// Without this the scan can run against unstyled markup and report bogus contrast.
function waitForStylesheets(doc: Document): Promise<void> {
  return new Promise((resolve) => {
    let attemptsLeft = STYLESHEET_POLL_ATTEMPTS;
    const poll = (): void => {
      const links = doc.querySelectorAll<HTMLLinkElement>(
        'link[rel="stylesheet"]'
      );
      let allLoaded = true;
      for (let i = 0; i < links.length; i++) {
        if (!links[i].sheet) {
          allLoaded = false;
          break;
        }
      }
      if (allLoaded || attemptsLeft-- <= 0) resolve();
      else setTimeout(poll, STYLESHEET_POLL_MS);
    };
    setTimeout(poll, 0);
  });
}

/**
 * Builds (or reuses) the sandbox iframe, renders `html` into it, and resolves
 * once styles have settled. The caller runs the scan against the returned
 * iframe so a single scan pipeline (and its stale-result guard) stays in charge.
 */
export async function prepareHtmlFragmentHost(
  options: HtmlFragmentHostOptions
): Promise<HTMLIFrameElement> {
  const iframe = ensureHost(options.hostId);
  const doc = iframe.contentDocument;
  if (!doc) throw new Error("Scan sandbox iframe is not accessible");

  const rootAttr = options.rootId ? ` id="${options.rootId}"` : "";
  const title = options.title ?? "Accessibility preview";
  doc.open();
  doc.write(
    `<!DOCTYPE html><html${rootAttr} lang="en"><head><meta charset="utf-8">` +
      `<title>${title}</title>${
        options.styleLinks ?? ""
      }</head><body></body></html>`
  );
  doc.close();

  const customCss = options.customCss;
  if (customCss && customCss.trim() !== "") {
    const styleEl = doc.createElement("style");
    styleEl.textContent = customCss;
    doc.head.appendChild(styleEl);
  }

  doc.body.innerHTML = sanitizeScanHtml(options.html);

  await waitForStylesheets(doc);
  return iframe;
}
